import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/hooks';
import { useBookAPI } from '../../../../hooks/useBookAPI';
import { useAlert } from '../../../../components/AlertBanner';
import { updateBookData } from '../../../../store/slices/bookSlice';
import { usePdfGeneration } from '../../../NotionEditor/hooks/usePdfGeneration';
import { PdfModal } from '../../../NotionEditor/components';
import { Section, BookMetadata } from '../../../NotionEditor/types';
import { BookData } from '../../../../store/slices/bookSlice';

// The only place in the app that can trigger a Puppeteer render for editor-content
// books — Preview lives here, not in the editor, so it's a bounded,
// once-per-formatting-pass action rather than clickable on unlimited demand while typing.
const BookPreviewStep: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showAlert } = useAlert();
  const { bookId, bookData, saveAndContinue, isSaving } = useBookAPI();

  const sections = (bookData.content?.json?.sections as Section[] | undefined) || [];
  const bookMetadata: BookMetadata = {
    author: bookData.author,
    subtitle: bookData.subtitle,
    isbn: bookData.isbn,
    contributors: bookData.contributors,
  };

  const isApproved = !!bookData.approvedContentHash && !bookData.pageCountApprovalStale;
  // Pre-existing/exempt books can still preview here — they're just never blocked
  // from continuing without approving, matching the backend gate's exemption.
  const requiresApproval = !!bookData.requiresPageCountApproval;

  // Unlike the upload/DOCX-import journeys, real section content already
  // exists by the time an author reaches this page (they wrote it in the
  // editor beforehand) — checking for content presence would be true on
  // every single visit. pageCount only gets set as a result of a *generated*
  // preview, so its absence is what actually means "no preview has ever
  // been generated for this book yet." Gates Meta Pixel's Lead event so
  // generating a preview for the Nth time doesn't refire it.
  const hadNoContentBefore = !bookData.pageCount;

  const {
    showPdfModal, pdfUrl, pdfZoom, pdfSource, isGeneratingPdf,
    generatePdf, closePdfModal, downloadPDF, handleZoomIn, handleZoomOut,
    lastPreview, isApprovingPageCount, approvePageCount
  } = usePdfGeneration({
    sections,
    bookSize: bookData.bookSize,
    bookLanguage: bookData.language,
    bookTitle: bookData.title,
    bookMetadata,
    bookId: bookId ?? undefined,
    hadNoContentBefore,
    onApproved: (pageCount, contentHash) => dispatch(updateBookData({
      pageCount,
      approvedPageCount: pageCount,
      approvedContentHash: contentHash,
      pageCountApprovalStale: false,
    })),
  });

  // Generation time scales with book length and isn't measured/bounded, so a
  // static "usually a few seconds" message would read as broken on a long book.
  const [isTakingLong, setIsTakingLong] = useState(false);
  useEffect(() => {
    if (!isGeneratingPdf) {
      setIsTakingLong(false);
      return;
    }
    const timer = setTimeout(() => setIsTakingLong(true), 7000);
    return () => clearTimeout(timer);
  }, [isGeneratingPdf]);

  // "Confirm and Continue" becomes usable once we have a page count to stand
  // behind: a fresh preview generated in this session, an already-approved count
  // from a prior visit (content unchanged since — this screen has no editor, so
  // that can't go stale while you're on it), or simply not being required at all.
  const canConfirmAndContinue = !!lastPreview || isApproved || !requiresApproval;

  const handleConfirmAndContinue = async () => {
    if (!canConfirmAndContinue) {
      showAlert('Please generate a preview first.', 'warning');
      return;
    }
    // A fresh preview this session still needs to be persisted as the approval —
    // an already-approved count from before needs no re-approval since content
    // can't have changed while on this screen.
    let freshApproval: Partial<BookData> | undefined;
    if (lastPreview) {
      const approved = await approvePageCount();
      if (!approved) return;
      // Pass the just-approved values explicitly rather than relying on
      // saveAndContinue's own `bookData` read — dispatch() updates the Redux
      // store, but this closure captured `bookData` at render time, before that
      // update landed, so the save below would otherwise resend the
      // pre-approval snapshot and overwrite the approval that just succeeded.
      freshApproval = {
        pageCount: lastPreview.pageCount,
        approvedPageCount: lastPreview.pageCount,
        approvedContentHash: lastPreview.contentHash,
        pageCountApprovalStale: false,
      };
    }
    const result: any = await saveAndContinue('book-cover', freshApproval);
    if (result?.meta?.requestStatus !== 'fulfilled') {
      showAlert(
        typeof result?.payload === 'string' ? result.payload : 'Failed to continue to Cover — please try again.',
        'error'
      );
    }
  };

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      <div className="hidden md:block mb-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
          <span>←</span><span>Go Back to Dashboard</span>
        </button>
      </div>

      <div className="md:hidden bg-gray-50 px-4 py-3 border-b border-gray-200">
        <p className="text-xs text-gray-600">Book Info › Book Form Factor › Book Formatting › Preview</p>
      </div>

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        <div className="max-w-xl mx-auto text-center space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Preview Your Book</h2>
          </div>

          {isApproved ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <p className="text-green-800 font-medium">
                ✓ You're all set — you can design your cover now.
              </p>
            </div>
          ) : lastPreview ? (
            // A preview was just generated this session but not yet confirmed —
            // reflect that immediately rather than showing a stale "not previewed
            // yet" message the moment the preview actually lands. Page count itself
            // isn't shown here — it's an internal detail, not something the user
            // needs to think about (it's still visible in the PDF viewer itself).
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <p className="text-blue-800 text-sm">
                Looks good! Click "Confirm and Continue" to move on to your cover.
              </p>
            </div>
          ) : requiresApproval ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <p className="text-amber-800 text-sm">
                {bookData.pageCountApprovalStale
                  ? 'Your book has changed since you last checked — please preview again before continuing.'
                  : 'Preview your book before continuing — this makes sure your cover fits perfectly.'}
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-600 text-sm">This step is optional — you're welcome to preview, or continue right away.</p>
            </div>
          )}

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate(`/notion?bookId=${bookId}`)}
              disabled={isGeneratingPdf}
              className="px-6 py-2.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm disabled:opacity-50"
            >
              Back to Editing
            </button>
            <button
              onClick={generatePdf}
              disabled={isGeneratingPdf}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-md hover:bg-gray-800 font-medium text-sm disabled:opacity-50"
            >
              {isGeneratingPdf ? 'Generating…' : lastPreview || isApproved ? 'Preview Again' : 'Generate Preview'}
            </button>
            {/* Only appears once we have a page count to stand behind — a fresh
                preview this session, or an already-approved one from before. */}
            {canConfirmAndContinue && (
              <button
                onClick={handleConfirmAndContinue}
                disabled={isGeneratingPdf || isSaving || isApprovingPageCount}
                className="bg-orange-500 text-white px-6 py-2.5 rounded-md hover:bg-orange-600 font-medium text-sm disabled:opacity-50"
              >
                {isApprovingPageCount ? 'Confirming…' : 'Confirm and Continue'}
              </button>
            )}
          </div>
        </div>
      </div>

      {isGeneratingPdf && (
        // Blocks the step sidebar and dashboard link too — they live in the parent
        // Book.tsx and have no awareness of this component's generating state, so a
        // fixed full-screen overlay is simpler than threading it up through props.
        <div className="fixed inset-0 z-50 bg-white/60 flex items-center justify-center cursor-wait">
          <div className="bg-white rounded-lg shadow-lg px-6 py-4 flex flex-col items-center gap-2 text-center max-w-xs">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
              <span className="text-sm font-medium text-gray-700">Generating your preview…</span>
            </div>
            <p className="text-xs text-gray-500">
              {isTakingLong
                ? 'Still working — longer books take a bit more time.'
                : "Checking your exact page count so your cover fits perfectly — this usually takes a few seconds."}
            </p>
          </div>
        </div>
      )}

      {showPdfModal && pdfUrl && (
        <PdfModal
          pdfUrl={pdfUrl}
          pdfSource={pdfSource}
          pdfZoom={pdfZoom}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onDownload={downloadPDF}
          onClose={closePdfModal}
          pageCount={lastPreview?.pageCount}
        />
      )}
    </div>
  );
};

export default BookPreviewStep;
